import React from 'react'

import {
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  LineElement,
  PointElement,
  TimeSeriesScale,
  Tooltip,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

import { ByTimeframeResponse } from '@pages/home/loaders/dashboard.loader'
import { formatDayAndMonth } from '@utils/formatters/date.formatter'
import { ThemeType } from '@utils/global'
import Storage from '@utils/storage'

ChartJS.register(LineElement, Tooltip, LinearScale, PointElement, TimeSeriesScale, CategoryScale)

const LINE_COLOR = '#70b6f8'
const NUM_OF_LINES = 4

interface Props {
  resp: ByTimeframeResponse
  timeIncrement: number
}

const ByTimeframeChart: React.FC<Props> = ({ resp: { data_points }, timeIncrement }) => {
  const chartData = data_points.values
  const chartLabels: string[] = []
  let total = data_points.offset // default total to be the offset
  let min = Number.MAX_SAFE_INTEGER
  let max = 0

  const today = new Date()
  const dayIncrement = timeIncrement / 60 / 60 / 24
  let dayCounter = 0
  const accData = chartData.map((value) => {
    const date = new Date(today)
    date.setDate(date.getDate() - dayCounter)
    chartLabels.unshift(formatDayAndMonth(date))
    dayCounter += dayIncrement

    total += value
    min = Math.min(min, total)
    max = Math.max(max, total)

    return total
  })

  // Improve axes
  // First check if there's any data, otherwise set everything to 0
  if (accData.length === 0) {
    max = 0
    min = 0
  }

  const stepSize = Math.max(1, Math.ceil((max - min) / NUM_OF_LINES))
  const bottomLine = Math.max(0, min - 1)
  const topLine = bottomLine + stepSize * (NUM_OF_LINES + 1)

  return (
    <>
      <span className="absolute top-1 right-2.5 text-[50px] leading-tight">{total}</span>
      <div className="w-full h-full">
        <Line
          data={{
            labels: chartLabels,
            datasets: [
              {
                xAxisID: 'xAxis',
                yAxisID: 'yAxis',
                data: accData,
                borderColor: LINE_COLOR,
                pointBorderColor: LINE_COLOR,
                pointBackgroundColor: Storage.theme === ThemeType.DARK ? '#31363A' : 'white',
                backgroundColor: 'transparent',
              },
            ],
          }}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                displayColors: false,
              },
            },
            scales: {
              xAxis: {
                type: 'category',
                grid: {
                  display: false,
                },
              },
              yAxis: {
                border: {
                  display: false,
                },
                type: 'linear',
                min: bottomLine,
                max: topLine,
                ticks: {
                  stepSize,
                },
                grid: {
                  color: Storage.theme === ThemeType.DARK ? '#4D4F56' : '#D1D5E0',
                  tickColor: Storage.theme === ThemeType.DARK ? '#4D4F56' : '#D1D5E0',
                },
              },
            },
          }}
          updateMode="resize"
        />
      </div>
    </>
  )
}

export default ByTimeframeChart
